import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner, Form, Table, Modal } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function PlannedPromotions() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data
  const [promotions, setPromotions] = useState([]);
  const [products, setProducts] = useState([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    promotion_name: '',
    promotion_type: 'discount',
    product_id: '',
    start_date: '',
    end_date: '',
    sale_price: '',
    estimated_sales: '',
    platform_fee_percent: '0',
    notes: ''
  });

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = async () => {
    setLoading(true);

    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    await Promise.all([
      fetchProducts(),
      fetchPromotions()
    ]);

    setLoading(false);
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch('/api/products/get-catalog', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data.catalog || []);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchPromotions = async () => {
    try {
      const response = await fetch('/api/planned-promotions/get-all', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPromotions(data.promotions || []);
      }
    } catch (err) {
      console.error('Error fetching promotions:', err);
    }
  };

  const handleOpenModal = (promotion = null) => {
    if (promotion) {
      // Editing existing
      setEditingPromotion(promotion);
      setFormData({
        promotion_name: promotion.promotion_name,
        promotion_type: promotion.promotion_type || 'discount',
        product_id: promotion.product_id.toString(),
        start_date: promotion.start_date ? promotion.start_date.split('T')[0] : '',
        end_date: promotion.end_date ? promotion.end_date.split('T')[0] : '',
        sale_price: promotion.sale_price.toString(),
        estimated_sales: promotion.estimated_sales.toString(),
        platform_fee_percent: (promotion.platform_fee_percent || 0).toString(),
        notes: promotion.notes || ''
      });
    } else {
      // New promotion
      setEditingPromotion(null);
      setFormData({
        promotion_name: '',
        promotion_type: 'discount',
        product_id: '',
        start_date: '',
        end_date: '',
        sale_price: '',
        estimated_sales: '',
        platform_fee_percent: '0',
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPromotion(null);
    setError('');
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-populate sale price when product is selected
    if (field === 'product_id' && value) {
      const selectedProduct = products.find(p => p.id.toString() === value);
      if (selectedProduct && !formData.sale_price) {
        // Suggest 20% off as default
        const suggestedPrice = (parseFloat(selectedProduct.sale_price) * 0.8).toFixed(2);
        setFormData(prev => ({
          ...prev,
          product_id: value,
          sale_price: suggestedPrice
        }));
      }
    }
  };

  const handleSavePromotion = async () => {
    // Validate
    if (!formData.promotion_name || !formData.product_id || !formData.start_date ||
        !formData.end_date || !formData.sale_price || !formData.estimated_sales) {
      setError('Please fill in all required fields');
      return;
    }

    if (new Date(formData.end_date) <= new Date(formData.start_date)) {
      setError('End date must be after start date');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        promotion_name: formData.promotion_name,
        promotion_type: formData.promotion_type,
        product_id: parseInt(formData.product_id),
        start_date: formData.start_date,
        end_date: formData.end_date,
        sale_price: parseFloat(formData.sale_price),
        estimated_sales: parseInt(formData.estimated_sales),
        platform_fee_percent: parseFloat(formData.platform_fee_percent || 0),
        notes: formData.notes
      };

      let response;
      if (editingPromotion) {
        response = await fetch(`/api/planned-promotions/update/${editingPromotion.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch('/api/planned-promotions/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        await fetchPromotions();
        handleCloseModal();
        setSuccess(editingPromotion ? 'Promotion updated!' : 'Promotion added!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save promotion');
      }
    } catch (err) {
      setError('Error saving promotion: ' + err.message);
    }

    setSaving(false);
  };

  const handleDeletePromotion = async (promotionId) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) {
      return;
    }

    try {
      const response = await fetch(`/api/planned-promotions/delete/${promotionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchPromotions();
        setSuccess('Promotion deleted');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete promotion');
      }
    } catch (err) {
      setError('Error deleting promotion: ' + err.message);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getProductById = (productId) => {
    return products.find(p => p.id === productId);
  };

  const calculateDiscount = (regularPrice, salePrice) => {
    if (!regularPrice || regularPrice === 0) return 0;
    return Math.round((1 - salePrice / regularPrice) * 100);
  };

  const calculateTotalRevenue = () => {
    return promotions.reduce((sum, promo) => {
      return sum + (promo.sale_price * promo.estimated_sales);
    }, 0);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading planned promotions...</p>
      </Container>
    );
  }

  if (products.length === 0) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          <Alert.Heading>Set Up Your Products First</Alert.Heading>
          <p>Before planning promotions, you need to add products to your catalog.</p>
          <Button variant="primary" onClick={() => navigate('/product-catalog')}>
            Go to Product Catalog
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="marketing-strategy" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <Card className="shadow mb-4">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h2 className="mb-1">Planned Promotions</h2>
                  <p className="text-muted mb-0">
                    Schedule promotional campaigns to boost sales. These will be factored into your exit strategy projections.
                  </p>
                </div>
                <Button variant="primary" onClick={() => handleOpenModal()}>
                  + Add Promotion
                </Button>
              </div>

              {promotions.length === 0 ? (
                <Alert variant="info">
                  <strong>No promotions planned yet.</strong> Click "Add Promotion" to schedule your first promotional campaign.
                </Alert>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table striped hover>
                      <thead>
                        <tr>
                          <th>Promotion</th>
                          <th>Type</th>
                          <th>Product</th>
                          <th>Date Range</th>
                          <th>Price</th>
                          <th style={{ whiteSpace: 'normal', minWidth: '60px' }}>Sales</th>
                          <th style={{ whiteSpace: 'normal', minWidth: '70px' }}>Gross</th>
                          <th style={{ whiteSpace: 'normal', minWidth: '60px' }}>Fees</th>
                          <th style={{ whiteSpace: 'normal', minWidth: '70px' }}>Net</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {promotions.map(promo => {
                          const product = getProductById(promo.product_id);
                          const regularPrice = product ? parseFloat(product.sale_price) : promo.product_regular_price;
                          const isLifetime = promo.promotion_type === 'lifetime_deal';
                          const hasFees = promo.platform_fee_percent > 0;

                          return (
                            <tr key={promo.id}>
                              <td>
                                <strong>{promo.promotion_name}</strong>
                                {promo.notes && (
                                  <small className="d-block text-muted">{promo.notes}</small>
                                )}
                              </td>
                              <td>
                                {isLifetime ? (
                                  <span className="badge bg-purple" style={{ backgroundColor: '#6f42c1' }}>Lifetime</span>
                                ) : (
                                  <span className="badge bg-warning text-dark">Discount</span>
                                )}
                              </td>
                              <td>
                                {promo.product_name}
                                {isLifetime && promo.product_purchase_type?.includes('recurring') && (
                                  <small className="d-block text-muted">
                                    (was ${regularPrice}/mo)
                                  </small>
                                )}
                              </td>
                              <td style={{ fontSize: '0.85em' }}>
                                {formatDate(promo.start_date)} -<br/>{formatDate(promo.end_date)}
                              </td>
                              <td>
                                ${promo.sale_price.toFixed(0)}
                              </td>
                              <td>{promo.estimated_sales}</td>
                              <td>${promo.gross_revenue?.toLocaleString() || (promo.sale_price * promo.estimated_sales).toLocaleString()}</td>
                              <td className={hasFees ? 'text-danger' : 'text-muted'}>
                                {hasFees ? (
                                  <>
                                    {promo.platform_fee_percent}%
                                    <small className="d-block">-${((promo.gross_revenue || promo.sale_price * promo.estimated_sales) - (promo.net_revenue || promo.gross_revenue)).toLocaleString()}</small>
                                  </>
                                ) : '-'}
                              </td>
                              <td className="text-success">
                                <strong>${(promo.net_revenue || promo.gross_revenue || promo.sale_price * promo.estimated_sales).toLocaleString()}</strong>
                              </td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => handleOpenModal(promo)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeletePromotion(promo.id)}
                                >
                                  Delete
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="table-info">
                          <td colSpan="6"></td>
                          <td><strong>${promotions.reduce((sum, p) => sum + (p.gross_revenue || p.sale_price * p.estimated_sales), 0).toLocaleString()}</strong></td>
                          <td className="text-danger">
                            -${(promotions.reduce((sum, p) => sum + (p.gross_revenue || p.sale_price * p.estimated_sales), 0) - promotions.reduce((sum, p) => sum + (p.net_revenue || p.gross_revenue || p.sale_price * p.estimated_sales), 0)).toLocaleString()}
                          </td>
                          <td><strong className="text-success">${promotions.reduce((sum, p) => sum + (p.net_revenue || p.gross_revenue || p.sale_price * p.estimated_sales), 0).toLocaleString()}</strong></td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>

                  <Alert variant="info" className="mt-3">
                    <strong>Note:</strong> These planned promotions will be included in your exit strategy revenue projections
                    and will help the AI provide more accurate growth forecasts.
                  </Alert>
                </>
              )}
            </Card.Body>
          </Card>

          <div className="d-flex justify-content-between">
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/dashboard/marketing-strategy')}
            >
              Back to Dashboard
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate('/dashboard/marketing-strategy')}
            >
              Continue
            </Button>
          </div>
        </Col>
      </Row>

      {/* Add/Edit Promotion Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingPromotion ? 'Edit Promotion' : 'Add Promotion'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label><strong>Promotion Name *</strong></Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Black Friday Sale, Summer Special, Early Bird Discount"
                value={formData.promotion_name}
                onChange={(e) => handleFormChange('promotion_name', e.target.value)}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Promotion Type *</strong></Form.Label>
                  <Form.Select
                    value={formData.promotion_type}
                    onChange={(e) => handleFormChange('promotion_type', e.target.value)}
                  >
                    <option value="discount">Discount Sale</option>
                    <option value="lifetime_deal">Lifetime Deal (e.g., AppSumo)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {formData.promotion_type === 'lifetime_deal'
                      ? 'Converts a recurring subscription to one-time lifetime access'
                      : 'Temporary price reduction on existing product'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Product *</strong></Form.Label>
                  <Form.Select
                    value={formData.product_id}
                    onChange={(e) => handleFormChange('product_id', e.target.value)}
                  >
                    <option value="">Select a product...</option>
                    {products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} (${parseFloat(product.sale_price).toFixed(2)}{product.purchase_type?.includes('recurring') ? '/mo' : ''})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Start Date *</strong></Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleFormChange('start_date', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>End Date *</strong></Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => handleFormChange('end_date', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>{formData.promotion_type === 'lifetime_deal' ? 'Lifetime Price *' : 'Sale Price *'}</strong>
                  </Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={formData.promotion_type === 'lifetime_deal' ? 'e.g., 47' : '0.00'}
                      value={formData.sale_price}
                      onChange={(e) => handleFormChange('sale_price', e.target.value)}
                    />
                  </div>
                  {formData.product_id && formData.sale_price && (() => {
                    const product = products.find(p => p.id.toString() === formData.product_id);
                    if (product) {
                      if (formData.promotion_type === 'lifetime_deal' && product.purchase_type?.includes('recurring')) {
                        const monthlyPrice = parseFloat(product.sale_price);
                        const lifetimePrice = parseFloat(formData.sale_price);
                        const monthsEquivalent = Math.round(lifetimePrice / monthlyPrice);
                        return (
                          <Form.Text className="text-muted">
                            Equivalent to {monthsEquivalent} months of subscription
                            {monthsEquivalent < 24 && <span className="text-warning ms-1">(consider if this is sustainable)</span>}
                          </Form.Text>
                        );
                      } else {
                        const discount = calculateDiscount(parseFloat(product.sale_price), parseFloat(formData.sale_price));
                        return (
                          <Form.Text className="text-muted">
                            Regular price: ${parseFloat(product.sale_price).toFixed(2)} |
                            <span className="text-danger ms-1">{discount}% off</span>
                          </Form.Text>
                        );
                      }
                    }
                    return null;
                  })()}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Estimated Sales *</strong></Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    placeholder="e.g., 50"
                    value={formData.estimated_sales}
                    onChange={(e) => handleFormChange('estimated_sales', e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Expected number of {formData.promotion_type === 'lifetime_deal' ? 'lifetime licenses' : 'sales'} during this promotion
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label><strong>Platform/Affiliate Fee %</strong></Form.Label>
              <div className="input-group">
                <Form.Control
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  placeholder="0"
                  value={formData.platform_fee_percent}
                  onChange={(e) => handleFormChange('platform_fee_percent', e.target.value)}
                />
                <span className="input-group-text">%</span>
              </div>
              <Form.Text className="text-muted">
                Common fees: AppSumo (70%), Affiliates (20-50%), Self-run (0-20% ad costs)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><strong>Notes</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Optional notes about this promotion..."
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
              />
            </Form.Group>

            {formData.sale_price && formData.estimated_sales && (() => {
              const gross = parseFloat(formData.sale_price) * parseInt(formData.estimated_sales);
              const feePercent = parseFloat(formData.platform_fee_percent || 0);
              const fees = gross * (feePercent / 100);
              const net = gross - fees;
              return (
                <Alert variant={feePercent > 0 ? 'warning' : 'success'}>
                  <div className="d-flex justify-content-between">
                    <span><strong>Gross Revenue:</strong> ${gross.toLocaleString()}</span>
                    {feePercent > 0 && <span className="text-danger">- Fees ({feePercent}%): ${fees.toLocaleString()}</span>}
                    <span className="text-success"><strong>Net to You: ${net.toLocaleString()}</strong></span>
                  </div>
                </Alert>
              );
            })()}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSavePromotion} disabled={saving}>
            {saving ? 'Saving...' : (editingPromotion ? 'Update Promotion' : 'Add Promotion')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default PlannedPromotions;
